'use client';
import { IconEye } from '@components/icons/IconEye';
import { UiKitButton } from '@components/UiKitButton';
import { UiKitInput } from '@components/UiKitInput';
import { UiKitLoadingButton } from '@components/UiKitLoadingButton';
import { loginFetch } from '@features/Auth/api/loginFetch';
import { useStaticContent } from '@providers/SiteDataContextProvider';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';
import { useRootStore } from 'src/providers';
import { email, pipe, safeParse, string } from 'valibot';
import styles from './Form.module.css';

const EmailSchema = pipe(
  string('EMAIL IS REQUIRED'),
  email('Invalid email address'),
);

type FormProps = {
  onSetForgot: () => void;
};

export const LogInForm = (props: FormProps) => {
  const { onSetForgot } = props;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailValidationError, setEmailValidationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const staticContent = useStaticContent();

  const store$ = useRootStore();

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const validateEmail = useCallback((emailValue: string): string => {
    const result = safeParse(EmailSchema, emailValue);
    if (!result.issues) return '';
    return result.issues.find((issue) => issue.type === 'email')?.message || '';
  }, []);

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const emailValue = e.target.value;
      setEmail(emailValue);

      if (emailValue.length > 3) {
        const validationError = validateEmail(emailValue);
        setEmailValidationError(validationError);
        if (validationError) setErrorMessage('');
      } else {
        setEmailValidationError('');
      }
    },
    [validateEmail],
  );

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const { mutateAsync: loginMutation } = useMutation({
    mutationFn: loginFetch,
    onMutate: useCallback(() => {
      setIsLoading(true);
      setErrorMessage('');
    }, []),
    onSuccess: useCallback(() => {
      store$?.user.email.set(email);
      typeof window !== 'undefined' && window.location.replace('/');
    }, [store$, email]),
    onError: useCallback(() => {
      setIsLoading(false);
      setErrorMessage('Incorrect email or password');
    }, []),
    retry: 0,
    gcTime: 0,
  });

  const handleFormSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      await loginMutation({ email, password });
    },
    [email, password, loginMutation],
  );

  return (
    <form onSubmit={handleFormSubmit} className={styles.form}>
      <div>
        <UiKitInput
          isError={!!emailValidationError}
          placeholder={staticContent.pages.auth.login.login_placeholder}
          fullWidth
          autoFocus
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          type="email"
          id="email"
          value={email}
          onChange={handleEmailChange}
          required
        />
        {emailValidationError && (
          <p className={styles.errorMessage}>{emailValidationError}</p>
        )}
      </div>
      <div className={styles.inputRow}>
        <UiKitInput
          placeholder={staticContent.pages.auth.login.password_placeholder}
          fullWidth
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          autoComplete="current-password"
          type={passwordVisible ? 'text' : 'password'}
          id="password"
          value={password}
          onChange={handlePasswordChange}
          required
        />
        <button
          className={classNames(styles.eyeButton, {
            [styles.isVisible]: passwordVisible,
          })}
          type="button"
          onClick={togglePasswordVisibility}
        >
          <IconEye />
        </button>
      </div>
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      <div className={styles.buttonsContainer}>
        <UiKitButton
          color="secondary"
          fullWidth
          type="button"
          className={styles.clearButton}
          onClick={(e) => {
            e.preventDefault();
            onSetForgot();
          }}
        >
          {staticContent.pages.auth.login.forgot_password}
        </UiKitButton>
        <UiKitLoadingButton
          color="primary"
          className={classNames(
            (email.length === 0 || password.length === 0) &&
              styles.submitButtonDisabled,
          )}
          fullWidth
          disabled={email.length === 0 || password.length === 0}
          loading={isLoading}
          type="submit"
        >
          LOG IN
        </UiKitLoadingButton>
      </div>
    </form>
  );
};
